import type { Keyword, KeywordScope, PapersResponse, Recipient, Status } from "../shared/types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Set by App so any 401 anywhere drops the UI back to the login screen. */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: init.body ? { "content-type": "application/json", ...init.headers } : init.headers,
  });

  if (response.status === 401 && path !== "/api/login" && path !== "/api/session") {
    onUnauthorized?.();
    throw new ApiError(401, "Session expired");
  }

  const text = await response.text();
  const data: unknown = text ? JSON.parse(text) : {};

  if (!response.ok) {
    const message =
      data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
        ? (data as { error: string }).error
        : `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }

  return data as T;
}

const body = (value: unknown): string => JSON.stringify(value);

export const api = {
  session: () => request<{ authenticated: boolean }>("/api/session"),

  login: (psk: string) =>
    request<{ authenticated: boolean }>("/api/login", { method: "POST", body: body({ psk }) }),

  logout: () => request<{ authenticated: boolean }>("/api/logout", { method: "POST", body: body({}) }),

  status: () => request<Status>("/api/status"),

  papers: (params: { keywordId: number | null; search: string; limit: number; offset: number }) => {
    const query = new URLSearchParams({ limit: String(params.limit), offset: String(params.offset) });
    if (params.keywordId !== null) query.set("keywordId", String(params.keywordId));
    if (params.search) query.set("search", params.search);
    return request<PapersResponse>(`/api/papers?${query.toString()}`);
  },

  sharePaper: (arxivId: string, emails: string[], note: string) =>
    request<{ delivered: number; rejected: { email: string; code: string }[] }>(
      `/api/papers/${encodeURIComponent(arxivId)}/share`,
      { method: "POST", body: body({ emails, note }) },
    ),

  keywords: () => request<{ keywords: Keyword[] }>("/api/keywords"),

  addKeyword: (term: string, scope: KeywordScope = "fulltext") =>
    request<{ keyword: Keyword }>("/api/keywords", { method: "POST", body: body({ term, scope }) }),

  deleteKeyword: (id: number) => request<{ ok: true }>(`/api/keywords/${id}`, { method: "DELETE" }),

  setKeywordActive: (id: number, active: boolean) =>
    request<{ ok: true }>(`/api/keywords/${id}`, { method: "PATCH", body: body({ active }) }),

  setKeywordScope: (id: number, scope: KeywordScope) =>
    request<{ ok: true }>(`/api/keywords/${id}`, { method: "PATCH", body: body({ scope }) }),

  recipients: () => request<{ recipients: Recipient[] }>("/api/recipients"),

  addRecipient: (email: string) =>
    request<{ recipient: Recipient }>("/api/recipients", { method: "POST", body: body({ email }) }),

  deleteRecipient: (id: number) => request<{ ok: true }>(`/api/recipients/${id}`, { method: "DELETE" }),

  setRecipientActive: (id: number, active: boolean) =>
    request<{ ok: true }>(`/api/recipients/${id}`, { method: "PATCH", body: body({ active }) }),

  run: () => request<{ started: boolean }>("/api/run", { method: "POST", body: body({}) }),
};
